/**
 * Wither skull projectiles: vanilla draws the skull model (an 8x8x8 head, the wither's centre-head
 * unwrap at texOffs 0,0) with the wither sheet (black) or the invulnerable sheet (blue), tumbling
 * toward its flight direction. Full-bright, like the fireballs.
 */

import * as THREE from 'three';
import type { EnvUniforms } from './env';
import type { WitherSkull } from '../entities/boss/skulls';
import { buildPart, entityMaterial, modelRoot, sheetTexture, type EntityMat } from './entities/core';

interface Inst { group: THREE.Group; mat: EntityMat; blue: boolean }

export class WitherSkullRenderer {
  readonly group = new THREE.Group();
  private pool: Inst[] = [];
  private live = new Map<number, Inst>();

  constructor(private readonly env: EnvUniforms) {}

  private make(blue: boolean): Inst {
    const mat = entityMaterial(this.env, sheetTexture(blue ? 'entity/wither/wither_invulnerable' : 'entity/wither/wither'), { emissive: true });
    const root = modelRoot();
    root.add(buildPart({ cubes: [{ tex: [0, 0], from: [-4, -4, -4], size: [8, 8, 8] }], pos: [0, 0, 0] }, 64, 64, mat));
    const group = new THREE.Group();
    group.add(root);
    // the model origin sits 1.5 blocks above the feet: centre it on the group origin
    root.position.y = 0;
    return { group, mat, blue };
  }

  update(list: readonly WitherSkull[], alpha: number): void {
    const seen = new Set<number>();
    for (const s of list) {
      seen.add(s.id);
      let i = this.live.get(s.id);
      if (!i) {
        const idx = this.pool.findIndex((p) => p.blue === s.blue);
        i = idx >= 0 ? this.pool.splice(idx, 1)[0] : this.make(s.blue);
        this.group.add(i.group);
        this.live.set(s.id, i);
      }
      i.group.visible = true;
      i.group.position.set(s.px + (s.x - s.px) * alpha, s.py + (s.y - s.py) * alpha, s.pz + (s.z - s.pz) * alpha);
      // face along the flight direction (vanilla: yaw/pitch lerped from the motion)
      const yaw = Math.atan2(-s.vx, -s.vz), pitch = Math.atan2(s.vy, Math.hypot(s.vx, s.vz));
      i.group.rotation.set(pitch, yaw, 0, 'YXZ');
    }
    for (const [id, i] of this.live) {
      if (seen.has(id)) continue;
      i.group.visible = false;
      this.live.delete(id);
      this.pool.push(i);
    }
  }

  dispose(): void {
    for (const i of [...this.pool, ...this.live.values()]) i.mat.dispose();
    this.group.clear();
    this.pool.length = 0; this.live.clear();
  }
}
